import React, {useEffect} from "react";
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";

// Options are identified by a client-only _id so reordering keeps each row's React
// state (focus, caret) attached to the right option. Callers strip _id when building
// the payload they save.
let optionIdCounter = 0;
const nextOptionId = () => `opt-${++optionIdCounter}`;

const needsNormalising = (options) =>
    options.some((option) => typeof option === "string" || !option || !option._id);

const normalise = (options) =>
    options.map((option) => {
        if (typeof option === "string") {
            return {name: option, value: option, _id: nextOptionId()};
        }
        if (!option) {
            return {name: "", value: "", _id: nextOptionId()};
        }
        return option._id ? option : {...option, _id: nextOptionId()};
    });

const SortableOption = ({option, onNameChange, onValueChange, onRemove}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: option._id});

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: "flex",
                alignItems: "center",
                marginBottom: "10px",
                padding: "5px",
                border: "1px solid #eee",
                borderRadius: "3px",
                backgroundColor: isDragging ? "#eef5ff" : "#fff",
                boxShadow: isDragging ? "0 2px 8px rgba(0, 0, 0, 0.15)" : "none",
                position: "relative",
                zIndex: isDragging ? 1 : "auto",
            }}
        >
            <span
                ref={setActivatorNodeRef}
                {...attributes}
                {...listeners}
                title="Drag to reorder"
                aria-label="Drag to reorder option"
                style={{
                    cursor: "grab",
                    marginRight: "8px",
                    marginTop: "20px",
                    padding: "0 4px",
                    color: "#999",
                    fontSize: "16px",
                    lineHeight: 1,
                    touchAction: "none",
                    userSelect: "none",
                }}
            >
                ⣿
            </span>
            <div style={{flex: 1, marginRight: "10px"}}>
                <label style={{fontSize: "12px", color: "#666", marginBottom: "2px"}}>
                    Display Name:
                </label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Nevada"
                    value={option.name || ""}
                    onChange={(e) => onNameChange(option._id, e.target.value)}
                    style={{fontSize: "12px"}}
                />
            </div>
            <div style={{flex: 1, marginRight: "10px"}}>
                <label style={{fontSize: "12px", color: "#666", marginBottom: "2px"}}>
                    System Value:
                </label>
                <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., NE"
                    value={option.value || ""}
                    onChange={(e) => onValueChange(option._id, e.target.value)}
                    style={{fontSize: "12px"}}
                />
            </div>
            <button
                className="btn btn-default clone-btn-right delete mb-1"
                type="button"
                onClick={() => onRemove(option._id)}
                style={{marginTop: "20px"}}
            >
                -
            </button>
        </div>
    );
};

const SortableOptionsEditor = ({options, onChange}) => {
    // Options arrive from the parent without ids (freshly added, prepopulated, or
    // loaded from a saved form), so they get tagged once before being rendered.
    useEffect(() => {
        if (needsNormalising(options)) {
            onChange(normalise(options));
        }
    }, [options, onChange]);

    const sensors = useSensors(
        // A small threshold keeps clicks into the text inputs from starting a drag.
        useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const handleNameChange = (id, name) => {
        onChange(
            options.map((option) => {
                if (option._id !== id) return option;
                // A system value still matching the display name was auto-copied, so keep
                // copying. Once the two diverge it was set deliberately and is left alone,
                // which also protects values loaded from an already saved field.
                const autoCopy = !option.value || option.value === option.name;
                return {...option, name, value: autoCopy ? name : option.value};
            })
        );
    };

    const handleValueChange = (id, value) => {
        onChange(options.map((option) => (option._id === id ? {...option, value} : option)));
    };

    const handleRemove = (id) => {
        onChange(options.filter((option) => option._id !== id));
    };

    const handleDragEnd = ({active, over}) => {
        if (!over || active.id === over.id) return;
        const from = options.findIndex((option) => option._id === active.id);
        const to = options.findIndex((option) => option._id === over.id);
        if (from === -1 || to === -1) return;
        onChange(arrayMove(options, from, to));
    };

    // Ids are assigned by the effect above; rendering is deferred one tick until then.
    if (needsNormalising(options)) return null;

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
                items={options.map((option) => option._id)}
                strategy={verticalListSortingStrategy}
            >
                {options.map((option) => (
                    <SortableOption
                        key={option._id}
                        option={option}
                        onNameChange={handleNameChange}
                        onValueChange={handleValueChange}
                        onRemove={handleRemove}
                    />
                ))}
            </SortableContext>
        </DndContext>
    );
};

export default SortableOptionsEditor;
